import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import * as announcementsApi from 'services/announcementsApi';
import { NewAnnouncement, SendAnnouncementState } from 'types/announcements';
import type { CreateAnnouncementPayload } from 'types/announcementsV1';

const initialState: SendAnnouncementState = {
    loading: false,
    error: false,
};

export type SendAnnouncementArgs = {
    parishId: string;
    announcement: NewAnnouncement;
    idempotencyKey?: string;
};

export const sendAnnouncement = createAsyncThunkWrapper<{ ok: true; parishId: string } | null, SendAnnouncementArgs>(
    'announcement/sendAnnouncement',
    async ({ parishId, announcement, idempotencyKey }, { getState }) => {
        if (!parishId) return null;
        const groupId = announcement.groupId || announcement.groupIds?.[0];
        if (!groupId && !(announcement.userIds && announcement.userIds.length)) {
            notify('Select an announcement group', 'error');
            return null;
        }
        const payload: CreateAnnouncementPayload = {
            title: announcement.title,
            message: announcement.message,
            ...(groupId ? { groupIds: [String(groupId)] } : {}),
            ...(announcement.userIds?.length ? { userIds: announcement.userIds } : {}),
        };
        try {
            await announcementsApi.createAnnouncement(parishId, payload, idempotencyKey);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Announcement sent successfully', 'success');
            return { ok: true as const, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to send announcement' }
);

const sendAnnouncementSlice = createSlice({
    name: 'sendAnnouncement',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(sendAnnouncement.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(sendAnnouncement.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(sendAnnouncement.rejected, (state, action) => {
            state.loading = false;
            state.error = action?.error?.message || true;
        });
    },
});

export default sendAnnouncementSlice.reducer;
